import { readonly, ref } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import { httpClientWithoutInterceptors } from '@/utils/httpClient'
import { createSecureQueryPersister } from '@/utils/secure-query-persister'
import { cryptoManager } from '@/utils/crypto'
import { clear } from 'idb-keyval'
import { retrieveEncryptionKeyResponseSchema } from '@/lib/schema/v2/encryption-key'
import { AppError } from '@/lib/schema/v2/error'

export function useSecureQueryPersistence() {
    const queryClient = useQueryClient()
    const initializing = ref(false)
    const isInitialized = ref(false)
    const initializationError = ref<AppError>(null)

    const initializePersistence = async () => {
        try {
            initializing.value = true

            const { data } = await httpClientWithoutInterceptors.get('/v1/user/encryption-key')

            const parsed = retrieveEncryptionKeyResponseSchema.parse(data)

            if (!parsed.success) {
                throw new Error(parsed.error?.title, {
                    cause: parsed.error,
                })
            }

            // Initialize crypto manager with server password
            await cryptoManager.initializeWithPassword(parsed.data?.key)

            // Create and apply persister
            const persister = createSecureQueryPersister()

            queryClient.setDefaultOptions({
                queries: {
                    gcTime: 1000 * 60 * 60 * 24 * 365,
                    staleTime: 1000 * 60 * 5,
                    persister,
                    retry: false,
                },
            })

            isInitialized.value = true
            initializationError.value = null
        } catch (error) {
            console.error('Failed to initialize secure query persistence:', error)
            initializationError.value = error
            isInitialized.value = false
        } finally {
            initializing.value = false
        }
    }

    const clearPersistedCache = async () => {
        try {
            await clear()
            cryptoManager.clearPassword()
            isInitialized.value = false
        } catch (error) {
            console.error('Failed to clear persisted cache:', error)
        }
    }

    return {
        initializing: readonly(initializing),
        isInitialized: readonly(isInitialized),
        initializationError: readonly(initializationError),
        initializePersistence,
        clearPersistedCache,
    }
}
